import { lightTheme } from "../../theme/theme";
type Theme = typeof lightTheme;

export const HomeStyles = (theme: Theme, isMobile: boolean) => {
  const accent = `var(--accent, ${theme.primary})`;
  const HEADER_H = 0;

  return {
    page: {
      color: theme.text,
      fontFamily: "'Poppins', sans-serif",
    },

    btnSecondary: {
      textDecoration: "none",
      backgroundColor: "transparent",
      color: "#fff",
      padding: "10px 16px",
      fontWeight: 700,
      border: `2px solid  #fff`,
      boxShadow: `0 0 0 2px ${theme.background}, 0 0 0 4px ${theme.primary}22`,
    },

    // ===== HERO (video fills the viewport only) =====
    hero: {
      position: "relative" as const,
      overflow: "hidden",
      height: "90vh",
      background: `linear-gradient(180deg, ${accent}22 0%, transparent 60%)`,
      paddingTop: HEADER_H ? `${HEADER_H}px` : 0,
    },
    heroBg: {
      position: "absolute" as const,
      inset: 0,
      zIndex: 0,
    },
    heroVideoBg: {
      position: "absolute" as const,
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover" as const,
    },
    heroImageBg: {
      position: "absolute" as const,
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover" as const,
      objectPosition: "center" as const,
    },
    heroOverlay: {
      position: "absolute" as const,
      inset: 0,
      background:
        "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.55) 100%)",
    },
    heroInner: {
      position: "relative" as const,
      zIndex: 1,
      maxWidth: 1140,
      margin: "0 auto",
      height: "100%",
      display: "flex",
      flexDirection: "column" as const,

      padding: "48px 16px",
    },

    heroContentBox: {
      maxWidth: 960,
    },

    heroBadge: {
      display: "inline-flex",
      alignItems: "start",
      gap: 8,
      padding: "6px 12px",
      borderRadius: 999,
      backgroundColor: "rgba(255,255,255,0.16)",
      border: `1px solid ${accent}66`,
      fontWeight: 600,
      fontSize: 12,
      letterSpacing: 0.6,
      textTransform: "uppercase" as const,
      color: "#fff",
      backdropFilter: "blur(2px)",
    },
    heroCTA: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap" as const,
    },

    sectionCategoriesTitle: {
      margin: "0 0 48px 0",
      fontFamily: "'Playfair Display', Georgia, serif",
      fontSize: "clamp(28px, 3.5vw, 42px)",
      fontWeight: 800,
      fontStyle: "italic",
      color: theme.text,
      lineHeight: 1.1,
      display: "inline-block",

      paddingBottom: 6,
    },
    sectionTitle: {
      fontSize: 30,
      marginBottom: 30,
      marginTop: 60,
      fontFamily: '"Playfair Display", Georgia, serif',
      fontWeight: 500,
      fontStyle: "italic",
    },
    // statas

    statsStrip: {
      maxWidth: 1140,
      padding: "12px 16px",
      borderRadius: 14,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
      gap: 10,
    },
    statCard: {
      padding: 12,
      borderRadius: 12,
      backgroundColor: "transparent",
      textAlign: "center" as const,
    },
    statValue: {
      fontFamily: '"Playfair Display", Georgia, serif',
      fontWeight: 700,
      fontStyle: "italic",
      fontSize: 24,
      lineHeight: 1.1,
      color: "#fff",
    },
    statLabel: { fontSize: 12, opacity: 0.8, marginTop: 4, color: "#fff" },

    heroStatsBar: {
      position: "absolute" as const,
      left: 0,
      right: 0,
      bottom: 20,
      zIndex: 2,
      margin: "0 auto",
      maxWidth: 1140,
      padding: "12px 16px",
      borderRadius: 16,
      background: "rgba(0, 0, 0, 0.35)",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
      border: "1px solid rgba(101, 101, 101, 0.28)",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
      gap: 10,
    },

    heroStatItem: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      justifyContent: "center",
      padding: "10px 8px",
      color: "#fff",
    },

    heroStatValue: {
      fontSize: 28,
      fontWeight: 800,
      lineHeight: 1.1,
      color: "#fff",
    },

    heroStatLabel: {
      marginTop: 4,
      fontSize: 12,
      letterSpacing: 0.6,
      textTransform: "uppercase" as const,
      color: "#fff",
      opacity: 0.95,
    },

    // ── Why our flags ──────────────────────────────────────────────────
    featuresList: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
      gap: 10,
    } as const,

    featuresListSm: {
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    } as const,

    featureItem: {
      display: "grid",
      gridTemplateColumns: "auto 1fr",
      alignItems: "start",
      columnGap: 10,
      padding: "10px 0",
    } as const,

    featureDot: {
      width: 8,
      height: 8,
      borderRadius: 999,
      backgroundColor: theme.dot,
      marginTop: 8,
      display: "inline-block",
      flexShrink: 0,
    } as const,
    // ===== Popular categories =====
    galleryGrid: {
      display: "flex",
      flexDirection: "column" as const,
      gap: isMobile ? 30 : 15,
      marginTop: 6,
    },
    galleryGridSm: {
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    },

    // ── How we work ────────────────────────────────────────────────────
    processStrip: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: 0,
      marginTop: 10,
      borderTop: `1px solid ${theme.border ?? "rgba(0,0,0,0.07)"}`,
    } as const,

    processStep: {
      padding: "28px 20px",
      borderInlineEnd: `1px solid ${theme.border ?? "rgba(0,0,0,0.07)"}`,
      fontSize: 14,
      lineHeight: 1.7,
      color: theme.text2 ?? theme.text,
      position: "relative" as const,
    } as const,

    // ===== Final CTA =====
    finalCTA: {
      marginTop: 28,
      padding: 18,
      borderRadius: 16,
      border: `1px solid ${accent}44`,
      background: `linear-gradient(180deg, ${accent}14 0%, ${accent}06 100%)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap" as const,
    },
    finalText: { fontWeight: 700, fontSize: 18 },

    // --- mobile tweaks ---
    heroInnerSm: {
      padding: "36px 16px",
      height: "100%",
    },
    heroCtaSm: {
      gap: 10,
      display: "flex",
      width: "100%",
      justifyContent: "center" as const,
    },

    sectionSm: { padding: "0 20px" },
    finalCtaSm: {
      flexDirection: "column" as const,
      alignItems: "stretch",
      gap: 10,
    },
    animatedTagline: {
      position: "absolute" as const,
      left: "50%" as const,
      bottom: "10vh" as const,
      transform: "translateX(-50%)",
      zIndex: 2 as const,
      textAlign: "center" as const,
      pointerEvents: "none" as const,
    },

    animatedText: {
      lineHeight: 1.3,
      fontSize: "clamp(16px, 2.5vw, 24px)",
      color: "#fff",
      textShadow: "0 2px 10px rgba(0,0,0,.35)",
      padding: "8px 14px",
      borderRadius: 10,
      fontFamily: '"Playfair Display", Georgia, serif',
      fontWeight: 500,
      fontStyle: "italic",
      animation: "fadeInOut 10.5s ease-in-out infinite",
    },
  };
};
